import React, { useState } from "react";
import api, { apiErr, downloadFile } from "@/lib/api";
import { noAutomaticRateFor } from "@/lib/fxPanel";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useParties } from "@/lib/parties";
import { Card, Btn, EmptyState, PageHeader, InfoTip } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { money, fmtDate, CURRENCY_FIELD_HELP, localISO } from "@/lib/format";
import { MoneyInput } from "@/lib/accountingFormat";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Warning, ShieldCheck, DownloadSimple, HandCoins } from "@phosphor-icons/react";
import { ViewButton, RecordView } from "@/components/RecordView";
import { envelopeFields, RECORD_TITLES } from "@/lib/recordFields";
import { Info } from "lucide-react";
import { CurrencySelect } from "@/components/CurrencySelect";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { useDateErrors } from "@/lib/useDateErrors";
import { HomeCurrencyEstimate } from "@/components/HomeCurrencyEstimate";
import { useFxCoverage, fxCoverageFor } from "@/lib/fxAdvisory";
import { curCode, isUSD } from "@/lib/currencies";

const REIMB = { expected: "Reimbursement expected", deferred: "Deferred — claim later", not_expected: "Not-Expected (Gift)" };

export default function Contributions() {
  const qc = useQueryClient();
  const { selectedStudentId } = useAuth();
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: envelopes = [] } = useQuery({
    queryKey: ["envelopes", selectedStudentId],
    queryFn: async () => (await api.get(`/envelopes${selectedStudentId ? `?student_id=${selectedStudentId}` : ""}`)).data,
  });
  const { data: totalData } = useQuery({
    queryKey: ["envelopes-total", selectedStudentId],
    queryFn: async () => (await api.get(`/envelopes/total${selectedStudentId ? `?student_id=${selectedStudentId}` : ""}`)).data,
  });
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  // The contribution behind the eye — read only. (ZEN-702)
  const [viewing, setViewing] = useState(null);
  const byCur = Object.entries(envelopes.reduce((m, e) => {
    const c = curCode(e.currency, "USD"); const d = m[c] || { ear: 0, rem: 0 };
    d.ear += e.usd_amount || 0; d.rem += e.remaining_usd || 0; m[c] = d; return m;
  }, {})).sort((a, b) => (a[0] === "USD" ? -1 : b[0] === "USD" ? 1 : a[0].localeCompare(b[0])));
  const refresh = () => { qc.invalidateQueries({ queryKey: ["envelopes"] }); qc.invalidateQueries({ queryKey: ["envelopes-total"] }); };
  const nameOf = (id) => students.find((s) => s.id === id)?.full_name || "—";
  const [delTarget, setDelTarget] = useState(null);
  const del = async (id) => { await api.delete(`/envelopes/${id}`); toast.success("Deleted"); refresh(); setDelTarget(null); };
  const exportCsv = async () => {
    try {
      await downloadFile("/envelopes/export", "contributions.csv",
        selectedStudentId ? { student_id: selectedStudentId } : undefined);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Contributions" subtitle="Earmarked money from a giver (e.g., Mom) that draws down as you tag expenses to it."
        actions={<div className="flex flex-wrap items-center gap-2">
          {envelopes.length > 0 && <Btn variant="ghost" onClick={exportCsv} data-testid="env-export-btn"><DownloadSimple size={16} weight="bold" /> Export CSV</Btn>}
          <Btn variant="green" onClick={() => { setEditing(null); setDlg(true); }} data-testid="add-envelope-btn"><Plus size={16} weight="bold" /> Add contribution</Btn>
        </div>} />

      {envelopes.length === 0 ? (
        <EmptyState icon={HandCoins} title="No contributions yet" subtitle="Record money a parent or relative gave toward a beneficiary for non-529 costs — like activity or sports fees. 529-eligible expenses are tracked on the Expenses page." />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="env-totals">
            {totalData && (
              <div className="rounded-md border border-[#034AA2]/30 bg-[#F0F6FF] px-3 py-1.5 text-center" data-testid="env-combined-total">
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#034AA2]">Combined total</div>
                <div className="text-base font-extrabold text-[#034AA2]">{money(totalData.total_usd, "USD")}</div>
                <HomeCurrencyEstimate estimate={totalData.home_estimate} />
              </div>
            )}
            {totalData?.manual_rate_count > 0 && (
              <button type="button" onClick={() => { const e = envelopes.find(x => x.fx_manual_required); if (e) { setEditing(e); setDlg(true); } }}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-2.5 py-1.5 text-xs font-medium text-[#92400E] hover:bg-[#FFF7E6] transition-colors"
                data-testid="env-manual-rate-note">
                <Warning size={14} weight="bold" className="shrink-0" />
                {totalData.manual_rate_count} contribution{totalData.manual_rate_count !== 1 ? "s" : ""} need{totalData.manual_rate_count === 1 ? "s" : ""} a manual rate — edit to fix
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {envelopes.map((e) => <EnvelopeCard key={e.id} e={e} studentName={nameOf(e.student_id)} onView={() => setViewing(e)} onEdit={() => { setEditing(e); setDlg(true); }} onDelete={() => setDelTarget(e.id)} />)}
          </div>
        </>
      )}

      <EnvelopeDialog open={dlg} onClose={() => setDlg(false)} editing={editing} students={students} defaultStudent={selectedStudentId} onSaved={refresh} />
      <RecordView
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={RECORD_TITLES.envelope}
        fields={viewing ? envelopeFields(viewing, { studentName: nameOf(viewing.student_id), reimbursementLabel: (k) => REIMB[k] }) : []}
        testid="envelope-view"
      />
      <DeleteConfirmDialog open={!!delTarget} onClose={() => setDelTarget(null)} onConfirm={() => del(delTarget)}
        title="Delete this envelope?" description="Tagged expenses keep their data but lose the link to this envelope." />
    </div>
  );
}

function EnvelopeCard({ e, studentName, onView, onEdit, onDelete }) {
  const amt = e.usd_amount ?? 0;
  const alloc = e.allocated_usd ?? 0;
  const rem = e.remaining_usd ?? 0;
  const pct = amt > 0 ? Math.min(100, Math.round((alloc / amt) * 100)) : 0;
  const over = rem < 0;
  const crossCurrency = e.currency && !isUSD(e.currency);
  return (
    <Card data-testid={`envelope-${e.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 font-bold">
            <HandCoins size={18} className="text-[#034AA2]" />
            {e.giver_nickname || "Giver"} → {studentName}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-1.5 text-[11px]">
            <span className="rounded-full bg-[#E9E9EB] px-2 py-0.5 font-semibold text-[#475569]">{REIMB[e.reimbursement]}</span>
            <span className="text-[#6b7280]">{fmtDate(e.date)}</span>
          </div>
        </div>
        <div className="flex shrink-0 gap-0.5">
          {/* The eye, immediately before the pencil. (ZEN-702) */}
          <ViewButton onClick={onView} testid={`envelope-view-${e.id}`} size={15} />
          <button onClick={onEdit} className="rounded p-1.5 text-[#034AA2] hover:bg-[#E9E9EB]" data-testid={`envelope-edit-${e.id}`}><PencilSimple size={15} weight="bold" /></button>
          <button onClick={onDelete} className="rounded p-1.5 text-[#F01919] hover:bg-[#E9E9EB]" data-testid={`envelope-del-${e.id}`}><Trash size={15} weight="bold" /></button>
        </div>
      </div>

      {crossCurrency && (
        <div className="mt-2 text-[11px] text-[#6b7280]" data-testid={`envelope-tracked-note-${e.id}`}>Gift {money(e.amount, e.currency)} · {money(amt, "USD")} at {fmtDate(e.date)} rate</div>
      )}

      <div className="mt-3">
        <div className="flex items-end justify-between text-sm">
          <span className="font-extrabold tnum text-xl" data-testid={`envelope-amount-${e.id}`}>{money(amt, "USD")}</span>
          <span className={`tnum font-semibold ${over ? "text-[#F01919]" : "text-[#077A39]"}`}>{over ? `over by ${money(-rem, "USD")}` : `${money(rem, "USD")} left`}</span>
        </div>
        <div className="mt-1 h-2 overflow-hidden rounded-full bg-[#E9E9EB]">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: over ? "#F01919" : "#034AA2" }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-[11px] text-[#6b7280]">{money(alloc, "USD")} used · {e.expense_count} expense{e.expense_count === 1 ? "" : "s"}</span>
        </div>
      </div>

      {e.fx_manual_required && (
        <div className="mt-2 flex items-center gap-1.5 rounded-md bg-[#FFF7E6] px-2.5 py-1.5 text-[11px] font-medium text-[#92600A]"><Warning size={13} weight="bold" /> No exchange rate available — edit to add a manual rate.</div>
      )}
      {e.violations > 0 && (
        <div className="mt-2 flex items-center gap-1.5 rounded-md bg-[#FBE9E9] px-2.5 py-1.5 text-xs font-semibold text-[#9F1010]" data-testid={`envelope-violation-${e.id}`}>
          <Warning size={14} weight="bold" /> {e.violations} tagged expense{e.violations === 1 ? "" : "s"} {e.violations === 1 ? "is" : "are"} 529-eligible — track {e.violations === 1 ? "it" : "them"} on the Expenses page, not here.
        </div>
      )}
      {e.violations === 0 && e.expense_count > 0 && (
        <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[#077A39]"><ShieldCheck size={14} weight="bold" /> All tagged expenses match the purpose</div>
      )}
      {e.reimbursement === "deferred" && e.deferred_note && (
        <div className="mt-2 text-xs text-[#475569]"><b>Deferred:</b> {e.deferred_note}</div>
      )}
      {e.note && <div className="mt-2 text-xs text-[#6b7280]">{e.note}</div>}
    </Card>
  );
}

function EnvelopeDialog({ open, onClose, editing, students, defaultStudent, onSaved }) {
  const { data: parties } = useParties();
  const [f, setF] = useState({});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const { onDateValidation, hasDateErrors } = useDateErrors();
  const giverOpts = [{ ref: "owner", nick: parties?.owner?.nickname || "Owner" }, ...(parties?.custom || []).map((p) => ({ ref: `party:${p.id}`, nick: p.nickname }))];

  // FX state (mirrors Gifts)
  // Which currencies convert on their own — the server's answer (ZEN-108).
  // Consulted only after a rate lookup has already failed, to decide whether
  // to tick the enter-the-rate-yourself box. Before the answer arrives,
  // `fxCoverageFor` answers "unknown", which ticks the box — the conservative
  // direction: the family can still record the contribution (ZEN-388).
  const { coverage: fxCoverage } = useFxCoverage();
  const envCur = curCode(f.currency, "USD");
  const isNonUSD = !isUSD(envCur);
  const [fetchingFx, setFetchingFx] = useState(false);
  const [fxFetchFailed, setFxFetchFailed] = useState(false);
  const [fxUsdField, setFxUsdField] = useState("");
  const fxFetchRef = React.useRef(0);

  React.useEffect(() => {
    if (open) {
      setF(editing
        ? { ...editing, fx_override: editing.fx_override || (editing.fx_source === "manual") || false }
        : { student_id: defaultStudent || "", giver_ref: "owner", amount: "", currency: "USD", date: localISO(), purpose: "not_eligible", reimbursement: "expected", deferred_note: "", note: "", fx_rate: null, fx_date: null, fx_override: false });
      setFxUsdField("");
      setFxFetchFailed(false);
    }
  }, [open, editing]); // eslint-disable-line

  // Auto-fetch FX rate when currency/date change (mirrors Gifts)
  React.useEffect(() => {
    if (!open || !isNonUSD || f.fx_override) return;
    const envDate = f.date;
    const envCurrency = f.currency;
    if (!envDate || !envCurrency) return;
    const fetchId = ++fxFetchRef.current;
    setFetchingFx(true);
    setFxFetchFailed(false);
    set("fx_rate", null);
    set("fx_date", null);
    api.post("/fx/preview", { date: envDate, base: envCurrency, target: "USD" })
      .then((res) => {
        if (fetchId === fxFetchRef.current) {
          set("fx_rate", res.data.rate);
          set("fx_date", res.data.rate_date || envDate);
          setFxFetchFailed(false);
        }
      })
      .catch(() => {
        if (fetchId === fxFetchRef.current) {
          setFxFetchFailed(true);
          if (fxCoverageFor(fxCoverage, envCurrency).state !== "auto") {
            set("fx_override", true);
          }
        }
      })
      .finally(() => { if (fetchId === fxFetchRef.current) setFetchingFx(false); });
  }, [f.currency, f.date, f.fx_override, open]); // eslint-disable-line

  const fxRate = parseFloat(f.fx_rate) || 0;
  const amt = parseFloat(f.amount) || 0;
  const usdEquiv = isNonUSD && fxRate ? amt * fxRate : amt;

  const save = async () => {
    if (hasDateErrors()) { toast.error("Fix the date errors before saving."); return; }
    if (!f.student_id) { toast.error("Select a beneficiary"); return; }
    if (!f.amount) { toast.error("Enter an amount"); return; }
    const giverNick = giverOpts.find((g) => g.ref === f.giver_ref)?.nick || "";
    const body = {
      ...f, amount: parseFloat(f.amount), giver_nickname: giverNick,
      // Not sent: the server works the US-dollar figure out from the rate, and a
      // figure rounded here could be $0.00, which it refuses (ZEN-825 review round 2).
      usd_amount: null,
      fx_rate: isNonUSD ? fxRate || null : null,
      fx_override: isNonUSD ? !!f.fx_override : false,
    };
    try {
      if (editing) await api.put(`/envelopes/${editing.id}`, body);
      else await api.post("/envelopes", body);
      toast.success(editing ? "Updated" : "Contribution added"); onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Contribution</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <L label="Beneficiary" full><select className="inp" value={f.student_id || ""} onChange={(e) => set("student_id", e.target.value)} data-testid="env-student"><option value="">Choose a beneficiary…</option>{students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</select></L>
          <L label="Paid by"><select className="inp" value={f.giver_ref || "owner"} onChange={(e) => set("giver_ref", e.target.value)} data-testid="env-giver">{giverOpts.map((g) => <option key={g.ref} value={g.ref}>{g.nick}</option>)}</select></L>
          <L label="Date"><UnifiedDateInput value={f.date || ""} onChange={(v) => set("date", v)} testid="env-date" preset="softFutureDate" onValidation={onDateValidation("env_date")} /></L>
          <L label={<>Currency <InfoTip testid="env-currency-info" text={CURRENCY_FIELD_HELP} /></>}><CurrencySelect value={curCode(f.currency, "USD")} onChange={(v) => { set("currency", v); if (isUSD(v)) { set("fx_rate", null); set("fx_date", null); set("fx_override", false); } }} testid="env-currency" /></L>
          <L label="Amount"><MoneyInput currency={curCode(f.currency, "USD")} value={f.amount ?? ""} onChange={(v) => set("amount", v)} testid="env-amount" /></L>

          {/* FX conversion panel (mirrors Gifts) */}
          {isNonUSD && (
            <div className="col-span-2 rounded-md border border-[#C2C9D1] bg-[#E9E9EB]/50 p-2.5" data-testid="env-fx-panel">
              {fxFetchFailed && f.date && (
                <div className="mb-2 rounded-md border border-[#D97706]/40 bg-[#FFFBEB] px-2.5 py-1.5" data-testid="env-rate-required">
                  <p className="text-xs font-medium text-[#92400E]">
                    <Warning size={12} weight="bold" className="mr-1 inline" />
                    {noAutomaticRateFor(envCur)}
                  </p>
                </div>
              )}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                  <Info size={14} className="text-[#034AA2]" /> Currency conversion
                </div>
                <label className="flex shrink-0 items-center gap-1.5 text-xs font-medium">
                  <input type="checkbox" checked={!!f.fx_override}
                    onChange={(e) => {
                      set("fx_override", e.target.checked);
                      if (!e.target.checked) { set("fx_rate", null); set("fx_date", null); setFxUsdField(""); }
                      else {
                        const r = parseFloat(f.fx_rate) || fxRate || 0;
                        const a = parseFloat(f.amount) || 0;
                        setFxUsdField(r > 0 && a > 0 ? (a * r).toFixed(2) : "");
                      }
                    }}
                    data-testid="env-fx-override" />
                  Manual override
                </label>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm">
                {f.fx_override ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-[#6b7280]">Rate</span>
                      <input type="number" step="0.0001" value={f.fx_rate || ""} onChange={(e) => {
                          set("fx_rate", e.target.value);
                          const r = parseFloat(e.target.value) || 0;
                          const a = parseFloat(f.amount) || 0;
                          setFxUsdField(r > 0 && a > 0 ? (a * r).toFixed(2) : "");
                        }}
                        data-testid="env-fx-rate-input" placeholder="e.g., 0.7213"
                        className="h-8 w-28 rounded border border-[#C2C9D1] px-2 text-sm" />
                    </label>
                    <span className="text-[#9aa1a9] text-xs font-medium">or</span>
                    <MoneyInput currency="USD" size="sm" className="!w-36" value={fxUsdField}
                      onChange={(v) => {
                        setFxUsdField(v);
                        const u = parseFloat(v) || 0;
                        const a = parseFloat(f.amount) || 0;
                        set("fx_rate", u > 0 && a > 0 ? String((u / a).toFixed(7)) : "");
                      }}
                      testid="env-fx-usd-direct" placeholder="e.g., 730.07" />
                  </div>
                ) : (
                  <span className="text-[#6b7280]">
                    {fetchingFx ? <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#034AA2] border-t-transparent" />Fetching rate…</span>
                      : fxRate ? <>
                          {`1 ${envCur} = ${Number(fxRate).toFixed(5)} USD · ${f.fx_source === "manual" ? "Manual" : f.fx_source === "treasury" ? "US Treasury" : "ECB"} · ${f.fx_date || ""}`}
                        </>
                        : <span className="italic text-[#F68F51]">Enter a date to auto-fetch rate</span>}
                  </span>
                )}
              </div>
              {fxRate > 0 && amt > 0 && (
                <div className="mt-1.5 text-xs text-[#6b7280]">
                  {f.fx_override
                    ? `≈ ${money(amt, envCur)} = ${money(usdEquiv, "USD")}`
                    : `≈ ${money(usdEquiv, "USD")}`}
                </div>
              )}
            </div>
          )}

          <L label="Reimbursement" full><select className="inp" value={f.reimbursement || "expected"} onChange={(e) => set("reimbursement", e.target.value)} data-testid="env-reimbursement"><option value="expected">Expected</option><option value="deferred">Deferred (e.g., reimbursed after graduation)</option><option value="not_expected">Not-Expected (Gift)</option></select></L>
          {f.reimbursement === "deferred" && (
            <L label="Deferred details — when is reimbursement expected?" full><input className="inp" value={f.deferred_note || ""} onChange={(e) => set("deferred_note", e.target.value)} data-testid="env-deferred-note" placeholder="e.g., After graduation in 2027, or once she has a summer job" /></L>
          )}
          <L label="Note (optional)" full><input className="inp" value={f.note || ""} onChange={(e) => set("note", e.target.value)} data-testid="env-note" /></L>
        </div>
        <DialogFooter><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn enterPrimary variant="green" onClick={save} disabled={!f.student_id} data-testid="env-save">Save</Btn></DialogFooter>
        <style>{`.inp{height:40px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 8px;font-size:14px;background:#fff}`}</style>
      </DialogContent>
    </Dialog>
  );
}
function L({ label, children, full }) { return <label className={`block ${full ? "col-span-2" : ""}`}><span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">{label}</span>{children}</label>; }
